class ManageSignatureController {
  constructor($uibModalInstance, $http, $rootScope, toastr, AuthenticationService, $timeout, $scope) {
    "ngInject";

    this.$uibModalInstance = $uibModalInstance;
    this.$http = $http;
    this.$rootScope = $rootScope;
    this.toastr = toastr;
    this.authenticationService = AuthenticationService;
    this.$timeout = $timeout;
    this.$scope = $scope;

    this.mode = 'upload';
    this.newSignatureFile = null;
    this.newSignatureFileName = '';
    this.previewSignature = $uibModalInstance.previewSignature || null;
    this.originalPreview = this.previewSignature;
    this.hasExistingSignature = !!$uibModalInstance.hasExistingSignature;

    this.fileInputId = `signature-upload-${Date.now()}`;
    this.canvasId = `signature-canvas-${Date.now()}`;
    this.boundFileChangeHandler = this.onFileChange.bind(this);

    this.availableFonts = [
      { label: 'Handwritten', value: "'Brush Script MT', cursive" },
      { label: 'Classic', value: "'Georgia', serif" },
      { label: 'Modern', value: "'Lucida Handwriting', cursive" },
      { label: 'Elegant', value: "'Dancing Script', cursive" },
      { label: 'Casual', value: "'Comic Sans MS', cursive" }
    ];
    this.typedSignatureText = '';
    this.typedSignatureFont = this.availableFonts[0].value;

    this.hasDrawn = false;
    this.canvasInitialized = false;
    this.isDrawing = false;
    this.canvasInitAttempts = 0; // Track initialization attempts
  }

  $onInit() {
    this.$timeout(() => {
      this.fileInputEl = document.getElementById(this.fileInputId);
      if (this.fileInputEl) {
        this.fileInputEl.addEventListener('change', this.boundFileChangeHandler);
      }
    });
  }

  $onDestroy() {
    if (this.fileInputEl) {
      this.fileInputEl.removeEventListener('change', this.boundFileChangeHandler);
    }
    this.detachCanvasListeners();
  }

  selectMode(mode) {
    if (this.mode === mode) {
      if (mode === 'draw') {
        // Re-initialize canvas if already in draw mode (in case canvas was removed/re-added)
        this.initCanvas();
      }
      return;
    }
    
    // Detach listeners from previous mode's canvas if switching away from draw
    if (this.mode === 'draw') {
      this.detachCanvasListeners();
    }
    
    const previousMode = this.mode;
    this.mode = mode;
    
    // Reset inputs based on the new mode
    // Don't reset hasDrawn if switching to draw mode (will be set when user draws)
    if (mode === 'draw') {
      this.newSignatureFile = null;
      this.newSignatureFileName = '';
      this.hasDrawn = false; // Reset for fresh drawing session
      this.previewSignature = this.originalPreview;
    } else {
      this.resetInputs();
    }

    if (mode === 'draw') {
      // Wait for Angular to render the canvas (ng-switch needs to process)
      // Use a longer delay to ensure the canvas element exists in the DOM
      this.$timeout(() => {
        this.initCanvas();
      }, 150);
    } else if (mode === 'type') {
      this.updateTypedPreview();
    } else {
      this.previewSignature = this.originalPreview;
    }
  }

  resetInputs() {
    this.newSignatureFile = null;
    this.newSignatureFileName = '';
    this.previewSignature = this.originalPreview;
    this.hasDrawn = false;
  }

  openFilePicker() {
    if (this.fileInputEl) {
      this.fileInputEl.click();
    }
  }

  onFileChange(event) {
    const files = event.target.files || [];
    if (!files.length) {
      this.newSignatureFile = null;
      this.newSignatureFileName = '';
      this.previewSignature = this.originalPreview;
      return;
    }

    this.newSignatureFile = files[0];
    this.newSignatureFileName = this.newSignatureFile.name;

    const reader = new FileReader();
    reader.onload = (e) => {
      this.previewSignature = e.target.result;
      this.$timeout(() => {});
    };
    reader.readAsDataURL(this.newSignatureFile);
  }

  initCanvas() {
    // Wait for Angular to render the canvas element (ng-switch needs time to process)
    this.$timeout(() => {
      this.canvasEl = document.getElementById(this.canvasId);
      if (!this.canvasEl) {
        // Canvas not found yet, try again after a longer delay (max 3 attempts)
        if (!this.canvasInitAttempts) {
          this.canvasInitAttempts = 0;
        }
        this.canvasInitAttempts++;
        if (this.canvasInitAttempts < 5) {
          console.log(`Canvas not found, retrying... (attempt ${this.canvasInitAttempts})`);
          this.$timeout(() => {
            this.initCanvas();
          }, 100);
        } else {
          console.error('Canvas element not found after multiple attempts');
        }
        return;
      }

      // Reset attempt counter on success
      this.canvasInitAttempts = 0;

      this.ctx = this.canvasEl.getContext('2d');
      this.ctx.lineWidth = 3;
      this.ctx.lineCap = 'round';
      this.ctx.strokeStyle = '#000';
      this.clearCanvas();

      // Always re-attach listeners (in case canvas was recreated)
      // First remove any existing listeners to avoid duplicates
      if (this.canvasInitialized && this.boundStartDraw) {
        this.detachCanvasListeners();
      }

      // Bind event handlers
      this.boundStartDraw = this.startDraw.bind(this);
      this.boundDraw = this.draw.bind(this);
      this.boundEndDraw = this.endDraw.bind(this);

      // Attach event listeners
      this.canvasEl.addEventListener('mousedown', this.boundStartDraw);
      this.canvasEl.addEventListener('mousemove', this.boundDraw);
      this.canvasEl.addEventListener('mouseup', this.boundEndDraw);
      this.canvasEl.addEventListener('mouseleave', this.boundEndDraw);

      this.canvasEl.addEventListener('touchstart', this.boundStartDraw, { passive: false });
      this.canvasEl.addEventListener('touchmove', this.boundDraw, { passive: false });
      this.canvasEl.addEventListener('touchend', this.boundEndDraw);

      this.canvasInitialized = true;
      console.log('Canvas initialized and event listeners attached. Canvas element:', this.canvasEl);
    }, 150); // Give Angular time to render the canvas
  }

  detachCanvasListeners() {
    if (this.canvasEl && this.canvasInitialized) {
      this.canvasEl.removeEventListener('mousedown', this.boundStartDraw);
      this.canvasEl.removeEventListener('mousemove', this.boundDraw);
      this.canvasEl.removeEventListener('mouseup', this.boundEndDraw);
      this.canvasEl.removeEventListener('mouseleave', this.boundEndDraw);
      this.canvasEl.removeEventListener('touchstart', this.boundStartDraw);
      this.canvasEl.removeEventListener('touchmove', this.boundDraw);
      this.canvasEl.removeEventListener('touchend', this.boundEndDraw);
      this.canvasInitialized = false;
    }
  }

  getCanvasPosition(event) {
    const rect = this.canvasEl.getBoundingClientRect();
    const clientX = event.touches ? event.touches[0].clientX : event.clientX;
    const clientY = event.touches ? event.touches[0].clientY : event.clientY;
    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  }

  startDraw(event) {
    if (!this.ctx || !this.canvasEl) {
      console.warn('Cannot start drawing: canvas not initialized');
      return;
    }
    event.preventDefault();
    this.isDrawing = true;
    
    // Update hasDrawn and trigger digest cycle to update Save button
    if (this.$scope.$$phase || this.$scope.$root.$$phase) {
      // Already in digest cycle, update directly
      this.hasDrawn = true;
    } else {
      // Not in digest cycle, use $apply
      this.$scope.$apply(() => {
        this.hasDrawn = true;
      });
    }
    
    const pos = this.getCanvasPosition(event);
    this.ctx.beginPath();
    this.ctx.moveTo(pos.x, pos.y);
  }

  draw(event) {
    if (!this.isDrawing) {
      return;
    }
    event.preventDefault();
    const pos = this.getCanvasPosition(event);
    this.ctx.lineTo(pos.x, pos.y);
    this.ctx.stroke();
    this.previewSignature = this.canvasEl.toDataURL('image/png');
    
    // Ensure hasDrawn is true (in case startDraw wasn't called for some reason)
    // Only trigger digest if hasDrawn just changed from false to true
    if (!this.hasDrawn) {
      if (this.$scope.$$phase || this.$scope.$root.$$phase) {
        this.hasDrawn = true;
      } else {
        this.$scope.$apply(() => {
          this.hasDrawn = true;
        });
      }
    }
  }

  endDraw(event) {
    if (!this.isDrawing) {
      return;
    }
    event && event.preventDefault();
    this.isDrawing = false;
    this.ctx.closePath();
    this.previewSignature = this.canvasEl.toDataURL('image/png');
  }

  clearCanvas() {
    if (this.ctx) {
      this.ctx.clearRect(0, 0, this.canvasEl.width, this.canvasEl.height);
    }
  }

  clearDrawing() {
    this.clearCanvas();
    this.hasDrawn = false;
    this.previewSignature = this.originalPreview;
  }

  updateTypedPreview() {
    if (!this.typedSignatureText || !this.typedSignatureText.trim()) {
      this.previewSignature = this.originalPreview;
      return;
    }
    const dataUrl = this.generateTypedSignature();
    this.previewSignature = dataUrl;
  }

  getTypedPreviewStyle() {
    return {
      'font-family': this.typedSignatureFont,
      'font-size': '48px',
      'color': '#000',
      'display': 'inline-block'
    };
  }

  generateTypedSignature() {
    const text = (this.typedSignatureText || '').trim();
    if (!text) {
      return null;
    }

    const canvas = document.createElement('canvas');
    const width = 600;
    const height = 200;
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = 'transparent';
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';
    ctx.fillStyle = '#000';
    ctx.font = `64px ${this.typedSignatureFont}`;
    ctx.fillText(text, width / 2, height / 2);
    return canvas.toDataURL('image/png');
  }

  dataURLtoBlob(dataurl) {
    const arr = dataurl.split(','), mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  }

  canSave() {
    if (this.mode === 'upload') {
      return !!this.newSignatureFile;
    }
    if (this.mode === 'draw') {
      return this.hasDrawn;
    }
    if (this.mode === 'type') {
      return !!(this.typedSignatureText && this.typedSignatureText.trim());
    }
    return false;
  }

  saveSignature() {
    let fd = new FormData();

    if (this.mode === 'upload') {
      if (!this.newSignatureFile) {
        return;
      }
      fd.append('file', this.newSignatureFile);
    } else if (this.mode === 'draw') {
      if (!this.hasDrawn || !this.canvasEl) {
        this.toastr.error('Please draw your signature first.', 'Signature Required');
        return;
      }
      const dataUrl = this.canvasEl.toDataURL('image/png');
      const blob = this.dataURLtoBlob(dataUrl);
      fd.append('file', new File([blob], 'signature.png', { type: 'image/png' }));
    } else if (this.mode === 'type') {
      const dataUrl = this.generateTypedSignature();
      if (!dataUrl) {
        this.toastr.error('Please enter your signature text.', 'Signature Required');
        return;
      }
      const blob = this.dataURLtoBlob(dataUrl);
      fd.append('file', new File([blob], 'signature.png', { type: 'image/png' }));
    }

    const config = {
      headers: { 'Content-Type': undefined },
      withCredentials: true,
      transformRequest: angular.identity,
    };

    this.$http.post(`${this.$rootScope.apiHost}/api/users/me/signature`, fd, config)
      .then(() => this.authenticationService.refreshUser())
      .then(() => {
        this.toastr.success('Signature updated successfully.', 'Signature Updated');
        this.$uibModalInstance.close(true);
      })
      .catch(() => {
        this.toastr.error('Could not update your signature.', 'ERROR');
      });
  }

  removeSignature() {
    this.$http.delete(`${this.$rootScope.apiHost}/api/users/me/signature`, { withCredentials: true })
      .then(() => this.authenticationService.refreshUser())
      .then(() => {
        this.toastr.success('Signature removed successfully.', 'Signature Removed');
        this.$uibModalInstance.close(true);
      })
      .catch(() => {
        this.toastr.error('Could not remove your signature.', 'ERROR');
      });
  }

  cancel() {
    this.$uibModalInstance.dismiss('cancel');
  }
}

export default ManageSignatureController;



// WEBPACK FOOTER //
// ./client/pages/profile/modals/manage-signature/manage-signature.controller.js